import { Box, Group, RingProgress, Skeleton, Stack, Text, ThemeIcon } from '@mantine/core'
import {
  IconAlertOctagon,
  IconBug,
  IconCircleCheck,
  IconMinus,
  IconTrendingDown,
  IconTrendingUp,
} from '@tabler/icons-react'
import type { JiraDashboardSummary, JiraVelocityData } from '@/api/jira'
import { CardHeader } from '@/components/ui/CardHeader'
import { WidgetCard } from '@/components/ui/WidgetCard'
import { useJiraSummary, useJiraVelocity } from '@/hooks/useJiraData'

// === Health Score Calculation ===

function computeHealthScore(summary: JiraDashboardSummary, velocity?: JiraVelocityData): number {
  let score = 100

  // Bugs penalty: -5 per open bug (max -30)
  score -= Math.min(summary.bugs_open * 5, 30)

  // Impediments penalty: -10 per impediment (max -30)
  score -= Math.min(summary.impediments * 10, 30)

  // Done ratio bonus/penalty
  const total = summary.total_issues || 1
  const doneRatio = (summary.by_status_category?.done || 0) / total
  if (doneRatio < 0.1) score -= 15       // Very low completion
  else if (doneRatio > 0.5) score += 5   // Good completion

  // Velocity trend
  if (velocity?.trend === 'declining') score -= 10
  else if (velocity?.trend === 'improving') score += 5

  return Math.max(0, Math.min(100, score))
}

// === Score Label + Color ===

interface ScoreLabel {
  label: string
  color: string
  ringColor: string
}

function getScoreLabel(score: number): ScoreLabel {
  if (score >= 80) return { label: 'Excelente', color: '#2f9e44', ringColor: 'green' }
  if (score >= 60) return { label: 'Bom', color: '#098d93', ringColor: 'teal' }
  if (score >= 40) return { label: 'Atenção', color: '#f59c1a', ringColor: 'orange' }
  return { label: 'Crítico', color: '#ff5b57', ringColor: 'red' }
}

// === Metric Row Component ===

interface MetricRowProps {
  icon: React.ReactNode
  iconColor: string
  label: string
  value: React.ReactNode
}

function MetricRow({ icon, iconColor, label, value }: MetricRowProps) {
  return (
    <Group gap="xs" wrap="nowrap">
      <ThemeIcon size="xs" variant="transparent" color={iconColor}>
        {icon}
      </ThemeIcon>
      <Text size="xs" c="dimmed" style={{ flex: 1, minWidth: 0 }}>
        {label}
      </Text>
      <Text size="xs" fw={600}>
        {value}
      </Text>
    </Group>
  )
}

// === Main Component ===

export default function JiraHealthScore() {
  const { data: summary, isLoading: summaryLoading, isError: summaryError } = useJiraSummary()
  const { data: velocity, isLoading: velocityLoading } = useJiraVelocity(6)

  if (summaryError || (!summaryLoading && !summary)) return null

  const isLoading = summaryLoading || velocityLoading

  const score = summary ? computeHealthScore(summary, velocity) : 0
  const { label, color, ringColor } = getScoreLabel(score)

  // Velocity metrics
  const velocityTrend = velocity?.trend ?? 'stable'
  const VelocityIcon =
    velocityTrend === 'improving'
      ? IconTrendingUp
      : velocityTrend === 'declining'
        ? IconTrendingDown
        : IconMinus
  const velocityColor =
    velocityTrend === 'improving' ? '#2f9e44' : velocityTrend === 'declining' ? '#ff5b57' : 'gray'
  const velocityLabel =
    velocityTrend === 'improving' ? 'Melhorando' : velocityTrend === 'declining' ? 'Declinando' : 'Estável'

  const bugsOpen = summary?.bugs_open ?? 0
  const impedimentos = summary?.impediments ?? 0

  return (
    <WidgetCard accent="primary" p={0}>
      <CardHeader title="Health Score" />

      <Box p="md">
        {isLoading ? (
          <Skeleton height={120} />
        ) : (
          <Group align="center" gap="xl" wrap="nowrap">
            {/* Left: Ring gauge */}
            <Stack align="center" gap={4} style={{ flexShrink: 0 }}>
              <RingProgress
                size={110}
                thickness={10}
                roundCaps
                sections={[{ value: score, color: ringColor }]}
                label={
                  <Stack align="center" gap={0}>
                    <Text fw={800} size="lg" lh={1} style={{ color }}>
                      {score}
                    </Text>
                    <Text size="10" c="dimmed" lh={1}>
                      /100
                    </Text>
                  </Stack>
                }
              />
              <Text size="sm" fw={700} style={{ color }}>
                {label}
              </Text>
            </Stack>

            {/* Right: Metrics list */}
            <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
              <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={2}>
                Métricas
              </Text>

              <MetricRow
                icon={<IconBug size={13} />}
                iconColor={bugsOpen > 0 ? '#ff5b57' : '#2f9e44'}
                label="Bugs"
                value={
                  <Text size="xs" fw={600} c={bugsOpen > 0 ? 'red' : 'green'}>
                    {bugsOpen}
                  </Text>
                }
              />

              <MetricRow
                icon={<IconAlertOctagon size={13} />}
                iconColor={impedimentos > 0 ? '#ff5b57' : '#2f9e44'}
                label="Impedimentos"
                value={
                  <Text size="xs" fw={600} c={impedimentos > 0 ? 'red' : 'green'}>
                    {impedimentos}
                  </Text>
                }
              />

              <MetricRow
                icon={<VelocityIcon size={13} />}
                iconColor={velocityColor}
                label="Velocity"
                value={
                  <Text size="xs" fw={600} style={{ color: velocityColor }}>
                    {velocityLabel}
                  </Text>
                }
              />

              <MetricRow
                icon={<IconCircleCheck size={13} />}
                iconColor="#2f9e44"
                label="SLA"
                value={
                  <Text size="xs" fw={600} c="green">
                    OK
                  </Text>
                }
              />
            </Stack>
          </Group>
        )}
      </Box>
    </WidgetCard>
  )
}
